import CONSTANTS from '@/helpers/constants';

const isPlainObject = (value: object): boolean => {
  const proto = Object.getPrototypeOf(value) as object | null;
  return proto === Object.prototype || proto === null;
};

const normalizeInternal = (value: unknown, seen: WeakMap<object, unknown>): unknown => {
  // Skip empty values.
  if (value === undefined || value === null) {
    return value;
  }

  // Normalize "DefaultValue" to an empty string.
  // @todo This should probably be normalized to null instead.
  if (value === CONSTANTS.DATA_VALUES.DEFAULT_STRING) {
    return '';
  }

  // Normalize arrays.
  if (Array.isArray(value)) {
    if (seen.has(value)) {
      return seen.get(value);
    }

    const array: unknown[] = [];
    seen.set(value, array);

    value.forEach((item, index) => {
      array[index] = normalizeInternal(item, seen);
    });

    return array;
  }

  // Ensure strings are trimmed.
  if (typeof value === 'string') {
    return value.trim();
  }

  // Normalize plain objects (skip class instances, DOM nodes, etc).
  if (typeof value === 'object') {
    const obj: object = value;

    if (!isPlainObject(obj)) {
      return value;
    }

    if (seen.has(obj)) {
      return seen.get(obj);
    }

    const newObj: Record<string, unknown> = {};
    seen.set(obj, newObj);

    Object.entries(obj as Record<string, unknown>).forEach(([key, entryValue]) => {
      newObj[key] = normalizeInternal(entryValue, seen);
    });

    return newObj;
  }

  // Return the original value.
  return value;
};

export const normalize = <T>(obj: T): T => normalizeInternal(obj, new WeakMap()) as T;

export const normalizeObjects = <
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  T extends Record<string, any>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  O extends Record<string, any>,
>(originalObject: T | null | undefined, newObject: O) => {
  let normalizedObject: Record<string, unknown> = {};
  if (originalObject === undefined || originalObject === null) {
    normalizedObject = normalize(newObject);
  }
  else {
    Object.entries(originalObject).forEach(([key, value]) => {
      normalizedObject[key] = normalize(newObject[key]) ?? normalize(value);
    });

    // Include new keys from newObject that don't exist on originalObject
    Object.entries(newObject).forEach(([key, value]) => {
      if (!(key in originalObject)) {
        normalizedObject[key] = normalize(value);
      }
    });
  }

  return <T><unknown> normalizedObject;
};
